import { useMemo, useState } from "react";
import { api } from "../api/client";
import type { AnalyticsEventRow } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { EmptyView, ErrorView, LoadingView } from "../components/StateView";
import { absoluteTime, relativeTime, shortDeviceId } from "../format";
import { useApi } from "../useApi";
import styles from "./AnalyticsPage.module.css";

export function AnalyticsPage() {
  const [filter, setFilter] = useState("");
  const state = useApi(() => api.feed("analytics", { limit: 500 }), []);

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="Raw event stream. Newest first, 500-row cap."
        right={
          <input
            type="search"
            placeholder="Filter event_type / app_id"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={styles.filter}
          />
        }
      />

      {state.kind === "loading" ? <LoadingView /> : null}
      {state.kind === "error" ? (
        <ErrorView message={state.message} onRetry={state.reload} />
      ) : null}
      {state.kind === "ready" ? (
        <EventTable rows={state.data.rows} filter={filter} />
      ) : null}
    </>
  );
}

function EventTable({
  rows,
  filter,
}: {
  rows: AnalyticsEventRow[];
  filter: string;
}) {
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.event_type.toLowerCase().includes(q) ||
        (r.app_id ?? "").toLowerCase().includes(q),
    );
  }, [rows, filter]);

  if (rows.length === 0) {
    return <EmptyView label="No events recorded yet." />;
  }
  if (filtered.length === 0) {
    return <EmptyView label={`No events match “${filter}”.`} />;
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.colTime}>When</th>
            <th className={styles.colType}>Event</th>
            <th className={styles.colApp}>App</th>
            <th className={styles.colVersion}>Suite</th>
            <th className={styles.colOs}>OS</th>
            <th className={styles.colDevice}>Device</th>
            <th className={styles.colPayload}>Payload</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.id}>
              <td className={styles.colTime} title={absoluteTime(r.created_at)}>
                {relativeTime(r.created_at)}
              </td>
              <td className={styles.colType}>
                <code className={styles.typeCell}>{r.event_type}</code>
              </td>
              <td className={styles.colApp}>{r.app_id ?? "—"}</td>
              <td className={styles.colVersion}>{r.suite_version ?? "—"}</td>
              <td className={styles.colOs}>{r.os ?? "—"}</td>
              <td className={styles.colDevice}>{shortDeviceId(r.device_id)}</td>
              <td className={styles.colPayload}>
                {r.payload ? (
                  <code className={styles.payloadCell}>{r.payload}</code>
                ) : (
                  <span className={styles.muted}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <footer className={styles.tableFooter}>
        Showing {filtered.length} of {rows.length} events
      </footer>
    </div>
  );
}
